<template>
  <div v-show="showDeviceTable">
    <q-card square flat bordered class="my-card bg-grey-1">
      <q-card-section style="padding: 0px">
        <q-table
          v-model:pagination="pagination"
          title="Devices"
          :rows="filteredDevices"
          :columns="columns"
          row-key="name"
          dense
          binary-state-sort
          :visible-columns="visibleColumns"
          :filter="filter"
          :filter-method="customFilter"
        >
          <template #body-cell="props">
            <q-td :props="props" @click="clickRow(props.row)">
              <div>{{ props.value }}</div>
            </q-td>
          </template>
          <template #top>
            <div
              class="col-2 q-table__title text-grey-brand text-bold"
              style="width: 150px"
            >
              Devices
            </div>
            <q-space />
            <q-select
              v-model="visibleColumns"
              multiple
              borderless
              dense
              options-dense
              emit-value
              map-options
              :display-value="$q.lang.table.columns"
              :options="columns"
              option-value="name"
              style="min-width: 50px"
            />
            <q-input v-model="filter" dense debounce="300" color="primary">
              <template #append>
                <q-icon name="search" />
              </template>
            </q-input>
            <q-btn
              class="q-ml-sm"
              dense
              :outline="!deviceListOnlineOnly"
              :color="deviceListOnlineOnly ? 'green' : 'grey'"
              icon="wifi"
              @click="toggleOnlineOnly"
            >
              <q-tooltip>{{
                deviceListOnlineOnly
                  ? 'Showing online only'
                  : 'Showing all devices'
              }}</q-tooltip>
            </q-btn>
          </template>
        </q-table>
      </q-card-section>
    </q-card>
  </div>
</template>

<script lang="ts">
import type { QTableColumn } from 'quasar';
import { defineComponent } from 'vue';
import { IDevice } from '@/models/Device';
import { hasAnyRole } from '@/utils/AuthGuard';

export default defineComponent({
  data() {
    return {
      pagination: {
        rowsPerPage: 15,
      },
      visibleColumns: ['uid', 'lastHeartbeat', 'lastPowerMode'],
      columns: [
        {
          name: 'uid',
          label: 'UID',
          field: 'uid',
          required: true,
          disable: true,
          sortable: true,
        },
        {
          name: 'lastHeartbeat',
          label: 'Last Heartbeat',
          field: 'lastHeartbeat',
          sortable: true,
        },
        {
          name: 'lastPowerMode',
          label: 'Mode',
          field: 'lastPowerMode',
          sortable: false,
        },
        {
          name: 'schedule',
          label: 'Schedule',
          field: 'schedule',
          sortable: false,
        },
      ] as QTableColumn<IDevice>[],

      filter: '',
    };
  },
  computed: {
    showDeviceTable(): boolean {
      return this.$store.state.showDeviceTable;
    },
    filteredDevices(): IDevice[] {
      const devices: IDevice[] = this.$store.state.device.devices || [];
      if (this.$store.state.deviceListOnlineOnly) {
        return devices.filter((d) => d.isConnected);
      }
      return devices;
    },
    deviceListOnlineOnly(): boolean {
      return this.$store.state.deviceListOnlineOnly;
    },
  },
  methods: {
    toggleOnlineOnly() {
      this.$store.commit('TOGGLE_DEVICE_LIST_ONLINE_ONLY');
    },
    clickRow(row: IDevice) {
      if (
        hasAnyRole(this.$store.state.user, 'viewer') &&
        !this.isUidInActivePigRunWithNotification(row.uid)
      ) {
        // If the user is a member of the viewer role, only dispatch the selectDevice action if the authenticated user clicked a uid value that is
        // participating in an active pig run that they have a notification subscription for.
        return;
      }

      this.$store.dispatch('selectDevice', { uid: row.uid, zoom: true });
    },

    isUidInActivePigRunWithNotification(uid: number): boolean {
      const list = this.$store.state.activePigRunUidArrayWithNotification;
      return (
        list != undefined &&
        list.length > 0 &&
        list.findIndex((element: number) => element == uid) != -1
      );
    },

    customFilter(rows: readonly IDevice[], terms: string) {
      return rows.filter((row: IDevice) => {
        return row.uid.toString() === terms;
      });
    },
  },
});
</script>
